import { useMemo } from 'react';
import { buildSiteHierarchyGroups, siteHierarchyHasChildren, type SiteTreeMeta } from '@/lib/catalogSiteTree';
import { cn } from '@/lib/cn';
import { SiteLevelBadge } from '@/components/SiteLevelBadge';

export type SiteHierarchyCheckboxSite = SiteTreeMeta & {
  site_code?: string;
  enabled?: boolean;
};

type Props = {
  sites: SiteHierarchyCheckboxSite[];
  selectedIds: number[];
  onToggle: (siteId: number) => void;
  isItemDisabled?: (siteId: number) => void;
  emptyHint?: string;
  showAutoIncludeHint?: boolean;
};

function level1CheckboxClass(disabled: boolean, checked: boolean): string {
  if (disabled) {
    return 'cursor-not-allowed border-slate-100 bg-slate-100/80 text-slate-400';
  }
  if (checked) {
    return 'border-teal-400/70 bg-teal-50/60 ring-1 ring-teal-500/15 hover:bg-teal-50/80';
  }
  return 'border-slate-200/90 bg-white hover:border-teal-200/80 hover:bg-slate-50/80';
}

function level2CheckboxClass(disabled: boolean, checked: boolean): string {
  if (disabled) {
    return 'cursor-not-allowed border-slate-100 bg-slate-100/70 text-slate-400';
  }
  if (checked) {
    return 'border-teal-400/80 bg-teal-50/70 text-teal-950 ring-1 ring-teal-500/15';
  }
  return 'border-slate-200/85 bg-white text-slate-800 hover:border-teal-300/80 hover:bg-slate-50';
}

export function SiteHierarchyCheckboxList({
  sites,
  selectedIds,
  onToggle,
  isItemDisabled,
  emptyHint = '\u672a\u52a0\u8f7d\u5230\u7ad9\u70b9',
  showAutoIncludeHint = true,
}: Props) {
  const groups = useMemo(() => buildSiteHierarchyGroups(sites), [sites]);
  const hasChildren = useMemo(() => siteHierarchyHasChildren(sites), [sites]);

  if (sites.length === 0) {
    return <p className="text-sm text-amber-800">{emptyHint}</p>;
  }

  return (
    <div className="space-y-3">
      {showAutoIncludeHint && hasChildren ? (
        <p className="rounded-lg border border-slate-200/90 bg-slate-50/90 px-3 py-2 text-xs leading-relaxed text-slate-600">
          {'\u52fe\u9009'}
          <strong className="font-medium text-slate-800">{'\u4e00\u7ea7\u7ad9\u70b9'}</strong>
          {'\u540e\uff0c\u5c0f\u7a0b\u5e8f\u5c06\u81ea\u52a8\u5c55\u793a\u5176\u4e0b\u5df2\u542f\u7528\u7684'}
          <strong className="font-medium text-slate-800">{'\u4e8c\u7ea7\u7ad9\u70b9'}</strong>
          {'\u4fe1\u606f\uff0c\u901a\u5e38\u65e0\u9700\u518d\u5355\u72ec\u52fe\u9009\u4e8c\u7ea7\u3002'}
        </p>
      ) : null}
      <ul className="space-y-3" role="group" aria-label={'\u5c55\u793a\u7ad9\u70b9\u5217\u8868'}>
        {groups.map((g) => {
          const groupKey = g.parent?.site.id ?? g.children.map((c) => c.site.id).join('-') ?? 'g';
          return (
            <li key={groupKey} className="space-y-2">
              {g.parent ? (
                <Level1Row
                  site={g.parent.site}
                  checked={selectedIds.includes(g.parent.site.id)}
                  disabled={isItemDisabled?.(g.parent.site.id) ?? false}
                  hasChildBlock={g.children.length > 0}
                  onToggle={() => onToggle(g.parent!.site.id)}
                />
              ) : null}
              {g.children.length > 0 ? (
                <div
                  className={cn(
                    g.parent && 'border-l-2 border-emerald-400/75 pl-3 sm:pl-4',
                  )}
                >
                  <div className="flex flex-wrap gap-2">
                    {g.children.map((child) => (
                      <Level2Chip
                        key={child.site.id}
                        site={child.site}
                        checked={selectedIds.includes(child.site.id)}
                        disabled={isItemDisabled?.(child.site.id) ?? false}
                        onToggle={() => onToggle(child.site.id)}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Level1Row({
  site,
  checked,
  disabled,
  hasChildBlock,
  onToggle,
}: {
  site: SiteTreeMeta & { site_code?: string };
  checked: boolean;
  disabled: boolean;
  hasChildBlock: boolean;
  onToggle: () => void;
}) {
  const code = 'site_code' in site && typeof site.site_code === 'string' ? site.site_code : '';

  return (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-sm shadow-sm transition',
        level1CheckboxClass(disabled, checked),
      )}
    >
      <input
        type="checkbox"
        className="h-4 w-4 shrink-0 rounded border-slate-300 text-teal-600 focus:ring-teal-500/30"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
      />
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <SiteLevelBadge depth={0} />
        <span className={cn('font-medium', disabled ? '' : 'text-slate-900')}>{site.site_name}</span>
        {code ? <span className="font-mono text-xs text-slate-400">{code}</span> : null}
        {hasChildBlock ? (
          <span className="text-xs text-slate-500">{'\u4e0b\u7ea7\u7ad9\u70b9\u5728\u4e0b\u65b9\u591a\u9009'}</span>
        ) : null}
      </span>
    </label>
  );
}

function Level2Chip({
  site,
  checked,
  disabled,
  onToggle,
}: {
  site: SiteTreeMeta;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={cn(
        'inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-sm transition',
        level2CheckboxClass(disabled, checked),
      )}
    >
      <input
        type="checkbox"
        className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-teal-600 focus:ring-teal-500/30"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
      />
      <SiteLevelBadge depth={1} className="!px-1 !py-px !text-[9px]" />
      <span className="truncate font-medium">{site.site_name}</span>
    </label>
  );
}
